import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { LogicBlockOrV2 } from 'dashboard/lib/workflows';
import {
  CaseLayout,
  CaseTemplateConfiguration,
  generateModuleToken,
  generateTabToken,
} from 'dashboard/models/case-template';
import { ResourceInquiryAttribute } from 'dashboard/modules/ResourceInquiry';
import {
  GovernmentIdVerificationModuleAttribute,
  SelfieVerificationModuleAttribute,
} from 'dashboard/modules/ResourceVerification/model';
import { ApiModel } from 'lib/interfaces/api';

const DEFAULT_CASE_LAYOUT_V2: CaseLayout = {
  tabs: [
    {
      name: 'Summary',
      token: generateTabToken(),
      modules: [
        {
          type: ModuleTypes.CaseInfo,
          token: generateModuleToken(),
          position: { x: 8, y: 0, width: 4, height: 45 },
          'module-layout': {
            'dynamic-height': true,
          },
          attributes: ['assignee', 'reference-ids', 'accounts', 'sla'],
        },
        {
          type: ModuleTypes.EventTimeline,
          token: generateModuleToken(),
          position: { x: 8, y: 115, width: 4, height: 70 },
          'module-layout': {
            'dynamic-height': true,
          },
        },
        {
          type: ModuleTypes.CaseComments,
          token: generateModuleToken(),
          position: { x: 8, y: 45, width: 4, height: 70 },
          'module-layout': {
            'dynamic-height': true,
          },
        },
        {
          type: ModuleTypes.Tags,
          token: generateModuleToken(),
          position: { x: 0, y: 0, width: 8, height: 25 },
          'module-layout': {
            'dynamic-height': true,
          },
        },
        {
          type: ModuleTypes.Resource,
          token: generateModuleToken(),
          position: { x: 0, y: 25, width: 8, height: 103 },
          'module-layout': {
            'dynamic-height': true,
          },
          'field-key': 'inquiries',
          attributes: [
            ResourceInquiryAttribute.Photos,
            ResourceInquiryAttribute.Name,
            ResourceInquiryAttribute.GovernmentIDNumber,
            ResourceInquiryAttribute.BirthDate,
            ResourceInquiryAttribute.Address,
            ResourceInquiryAttribute.ExpirationDate,
            ResourceInquiryAttribute.EmailAddress,
            ResourceInquiryAttribute.PhoneNumber,
            ResourceInquiryAttribute.SessionLocation,
            ResourceInquiryAttribute.Fields,
            ResourceInquiryAttribute.DocumentExtraction,
          ],
          'default-accordion-open': true,
        },
      ],
    },
    {
      name: 'Verifications',
      token: generateTabToken(),
      modules: [
        {
          type: ModuleTypes.Resource,
          token: generateModuleToken(),
          position: { x: 6, y: 0, width: 6, height: 133 },
          'module-layout': {
            'dynamic-height': true,
          },
          'field-key': 'verifications',
          attributes: [
            SelfieVerificationModuleAttribute.Photos,
            SelfieVerificationModuleAttribute.CaptureMethod,
            SelfieVerificationModuleAttribute.Checks,
          ],
          'verification-type': ApiModel.VerificationSelfie,
          'default-accordion-open': true,
          'verification-templates': [],
        },
        {
          type: ModuleTypes.Resource,
          token: generateModuleToken(),
          position: { x: 0, y: 0, width: 6, height: 133 },
          'module-layout': {
            'dynamic-height': true,
          },
          'field-key': 'verifications',
          attributes: [
            GovernmentIdVerificationModuleAttribute.Photos,
            GovernmentIdVerificationModuleAttribute.Id,
            GovernmentIdVerificationModuleAttribute.FullName,
            GovernmentIdVerificationModuleAttribute.BirthDate,
            GovernmentIdVerificationModuleAttribute.IssuingCountry,
            GovernmentIdVerificationModuleAttribute.IssueDate,
            GovernmentIdVerificationModuleAttribute.ExpirationDate,
            GovernmentIdVerificationModuleAttribute.CaptureMethod,
            GovernmentIdVerificationModuleAttribute.CaptureMethodBack,
            GovernmentIdVerificationModuleAttribute.Address,
            GovernmentIdVerificationModuleAttribute.Nationality,
            GovernmentIdVerificationModuleAttribute.Checks,
          ],
          'verification-type': ApiModel.VerificationGovernmentId,
          'default-accordion-open': true,
          'verification-templates': [],
        },
      ],
    },
    {
      name: 'Device Metadata',
      token: generateTabToken(),
      modules: [
        {
          type: ModuleTypes.Device,
          token: generateModuleToken(),
          position: { x: 0, y: 16, width: 6, height: 100 },
          'module-layout': {
            'dynamic-height': true,
          },
          attributes: [
            'type',
            'os',
            'browser',
            'browser-fingerprint',
            'device-vendor-id',
            'device-handoff-method',
            'ip-address',
            'threat-level',
            'country-name',
            'region-name',
            'latitude',
            'longitude',
            'is-tor',
            'is-proxy',
            'is-datacenter',
            'is-isp',
            'is-vpn',
          ],
        },
        {
          type: ModuleTypes.Location,
          token: generateModuleToken(),
          position: { x: 6, y: 0, width: 6, height: 116 },
          'module-layout': {
            'dynamic-height': true,
          },
          'custom-locations': undefined,
        },
        {
          type: ModuleTypes.Tags,
          token: generateModuleToken(),
          position: { x: 0, y: 0, width: 6, height: 16 },
          'module-layout': {
            'dynamic-height': true,
          },
        },
        {
          type: ModuleTypes.Resource,
          token: generateModuleToken(),
          position: { x: 0, y: 116, width: 6, height: 70 },
          'module-layout': {
            'dynamic-height': true,
          },
          'field-key': 'verifications',
          attributes: [GovernmentIdVerificationModuleAttribute.Address],
          'verification-type': ApiModel.VerificationGovernmentId,
          'default-accordion-open': true,
          'verification-templates': [],
        },
      ],
    },
  ],
  'layout-type': 'grid',
};

export const DEFAULT_CONDITIONAL: LogicBlockOrV2 = { or: [] };

export const DEFAULT_DISPLAY_CONDITIONAL: LogicBlockOrV2 = {
  or: [
    {
      and: [{}],
    },
  ],
};

export const generateDefaultCaseConfiguration = (): CaseTemplateConfiguration => {
  return {
    resolutions: ['Approved', 'Declined'],
    'assignment-policies': [],
    'triage-configuration': {
      'unassign-cases-when-unavailable': false,
      'policy-tokens-created-cases': [],
      'policy-tokens-unassigned-cases': [],
    },
    subscriptions: {
      'case-creation-email-addresses': [],
    },
    'checklist-items': [],
    'field-schema': [],
    'case-action-ids': [],
    'case-message-templates': [],
    states: [],
    'state-tags': [],
    'case-layout-v2': DEFAULT_CASE_LAYOUT_V2,
    'case-comment-templates': [],
    'sar-narrative-templates': [],
    'default-from-email-address': '',
  } as CaseTemplateConfiguration;
};
